/**
 * Account Security Component
 *
 * ISO 27001 A.9.4.3 - User security management
 * Page de gestion de la sécurité du compte utilisateur personnel
 *
 * Fonctionnalités:
 * - Changement de mot de passe
 * - Configuration 2FA
 * - Gestion des appareils connectés
 * - Comptes OAuth liés
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Lock,
  Smartphone,
  Monitor,
  Link2,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { ChangePasswordForm } from './ChangePasswordForm';
import { DevicesManagement } from './DevicesManagement';
import { LinkedAccountsSettings } from './LinkedAccountsSettings';
import { TwoFactorSetup } from './TwoFactorSetup';
import { useAuthStore } from '../hooks/useAuth';
import { twoFactorApi } from '../api/two-factor.api';
import { oauthAPI } from '@/utils/api';
import { logger } from '@/utils/logger';

type ActiveSection = 'overview' | 'password' | 'devices' | 'linked-accounts' | '2fa';

export interface AccountSecurityProps {
  /** Classe CSS additionnelle */
  className?: string;
  /** Section initialement active */
  initialSection?: ActiveSection;
}

export const AccountSecurity: React.FC<AccountSecurityProps> = ({
  className = '',
  initialSection = 'overview',
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<ActiveSection>(initialSection);
  const { user } = useAuthStore();

  // Security status state
  const [securityStatus, setSecurityStatus] = useState({
    has2FA: false,
    linkedAccountsCount: 0,
    loading: true,
  });

  // Fetch security status on mount
  useEffect(() => {
    const fetchSecurityStatus = async () => {
      try {
        const [twoFactorStatus, linkedAccounts] = await Promise.all([
          twoFactorApi.getStatus().catch((err) => {
            logger.debug('2FA status not available:', err);
            return { enabled: false };
          }),
          oauthAPI.getLinkedAccounts().catch((err) => {
            logger.debug('Linked accounts not available:', err);
            return [];
          }),
        ]);

        setSecurityStatus({
          has2FA: twoFactorStatus.enabled,
          linkedAccountsCount: linkedAccounts.length,
          loading: false,
        });

        logger.debug('Security status loaded:', {
          has2FA: twoFactorStatus.enabled,
          linkedAccountsCount: linkedAccounts.length,
        });
      } catch (error) {
        logger.error('Failed to fetch security status:', error);
        setSecurityStatus((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchSecurityStatus();
  }, []);

  // Refresh status when returning to overview
  const handleSectionChange = (section: ActiveSection) => {
    setActiveSection(section);
    if (section === 'overview') {
      // Refresh status when going back to overview
      setSecurityStatus((prev) => ({ ...prev, loading: true }));
      Promise.all([
        twoFactorApi.getStatus().catch(() => ({ enabled: false })),
        oauthAPI.getLinkedAccounts().catch(() => []),
      ]).then(([twoFactorStatus, linkedAccounts]) => {
        setSecurityStatus({
          has2FA: twoFactorStatus.enabled,
          linkedAccountsCount: linkedAccounts.length,
          loading: false,
        });
      });
    }
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 overflow-hidden hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  const menuItemClasses = (isActive: boolean) =>
    `flex items-center justify-between p-4 cursor-pointer transition-all duration-200
    ${isActive
      ? 'bg-teal-50/50 border-l-4 border-teal-500'
      : 'hover:bg-white/50 border-l-4 border-transparent'
    }`;

  // Security status from fetched data
  const { has2FA, linkedAccountsCount, loading: statusLoading } = securityStatus;
  const securityScore = has2FA ? 100 : 60;

  const renderContent = () => {
    switch (activeSection) {
      case 'password':
        return (
          <div className="p-6">
            <ChangePasswordForm
              compact={true}
              userEmail={user?.email}
              userFullName={user?.fullName}
              isAdmin={user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'SUPERADMIN'}
              onSuccess={() => handleSectionChange('overview')}
              onCancel={() => handleSectionChange('overview')}
            />
          </div>
        );

      case 'devices':
        return (
          <div className="p-6">
            <DevicesManagement />
          </div>
        );

      case 'linked-accounts':
        return (
          <div className="p-6">
            <LinkedAccountsSettings />
          </div>
        );

      case '2fa':
        return (
          <div className="p-6">
            <TwoFactorSetup
              onEnabled={() => handleSectionChange('overview')}
              onDisabled={() => handleSectionChange('overview')}
            />
          </div>
        );

      default:
        return (
          <div className="p-6 space-y-4">
            {/* Security Score */}
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-5 border border-teal-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-xl">
                    <Shield className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t('auth.security.securityScore')}</p>
                    <p className="text-xs text-gray-500">{t('auth.security.scoreEvaluation')}</p>
                  </div>
                </div>
                {statusLoading ? (
                  <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
                ) : (
                  <div className={`text-2xl font-bold ${
                    securityScore >= 80 ? 'text-green-600' :
                    securityScore >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {securityScore}%
                  </div>
                )}
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    securityScore >= 80 ? 'bg-green-500' :
                    securityScore >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: statusLoading ? '0%' : `${securityScore}%` }}
                />
              </div>
              {!statusLoading && !has2FA && (
                <p className="text-xs text-teal-600 mt-2">
                  {t('auth.security.improve2FAScore')}
                </p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('auth.security.quickActions')}</h4>

              {/* Password */}
              <button
                onClick={() => handleSectionChange('password')}
                className={menuItemClasses(false) + ' w-full rounded-xl bg-white/30'}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Lock className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.security.changePassword')}</p>
                    <p className="text-xs text-gray-500">{t('auth.security.changePasswordSubtitle')}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>

              {/* 2FA */}
              <button
                onClick={() => handleSectionChange('2fa')}
                className={menuItemClasses(false) + ' w-full rounded-xl bg-white/30'}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${has2FA ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <Smartphone className={`w-4 h-4 ${has2FA ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {t('auth.security.twoFactorAuth')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {statusLoading ? t('auth.security.loading') : has2FA ? t('auth.security.twoFAEnabled') : t('auth.security.twoFANotConfigured')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {statusLoading ? (
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  ) : has2FA ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>

              {/* Devices */}
              <button
                onClick={() => handleSectionChange('devices')}
                className={menuItemClasses(false) + ' w-full rounded-xl bg-white/30'}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Monitor className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.security.connectedDevices')}</p>
                    <p className="text-xs text-gray-500">{t('auth.security.manageDevices')}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>

              {/* Linked Accounts */}
              <button
                onClick={() => handleSectionChange('linked-accounts')}
                className={menuItemClasses(false) + ' w-full rounded-xl bg-white/30'}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${linkedAccountsCount > 0 ? 'bg-green-100' : 'bg-purple-100'}`}>
                    <Link2 className={`w-4 h-4 ${linkedAccountsCount > 0 ? 'text-green-600' : 'text-purple-600'}`} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.security.linkedAccounts')}</p>
                    <p className="text-xs text-gray-500">
                      {statusLoading ? t('auth.security.loading') : linkedAccountsCount > 0
                        ? (linkedAccountsCount > 1
                          ? t('auth.security.linkedAccountsCountPlural', { count: linkedAccountsCount })
                          : t('auth.security.linkedAccountsCount', { count: linkedAccountsCount }))
                        : t('auth.security.linkedAccountsDefault')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {linkedAccountsCount > 0 && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Shield className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('auth.security.title')}</h2>
          <p className="text-gray-500">{t('auth.security.subtitle')}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className={cardClasses}>
        {/* Section Header with Back Button */}
        {activeSection !== 'overview' && (
          <div className="bg-gray-50/50 border-b border-gray-100 px-6 py-4">
            <button
              onClick={() => handleSectionChange('overview')}
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              {t('auth.security.backToOverview')}
            </button>
          </div>
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default AccountSecurity;
